// Imports
import React, { useContext, useEffect } from 'react';
import { useAppDispatch } from '../../../shareables/foundation/front-end/redux/hooks';
import Box from '../../../shareables/foundation/admin-ui/components/content/box';
import Table from '../../../shareables/foundation/admin-ui/components/content/table';
import apiActions from '../../../shareables/integrations/api/redux/actions';
import MultiFieldContainer from '../../../shareables/foundation/front-end/components/forms/multi-field-container';
import LoadingRow from '../../../shareables/foundation/front-end/components/tables/loading-row';
import usePagination from '../../../shareables/foundation/front-end/utils/use-pagination';
import ActivityLogRow from './row';
import { GetActivitiesResponse } from '../../../shareables/types/api/main/reseller/activities/get';
import ResellerContext from '../../resources/reseller-provider/context';


// Define the accepted props
interface Props {
	initialActivities?: GetActivitiesResponse;
	storybookPageSize?: number;
	initialSearchQuery?: string;
	pageSizes: number[];
	onActivitiesChange?: (activities: GetActivitiesResponse) => void;
}


// Function component
const ActivityLogTable: React.FC<Props> = ({
	initialActivities,
	storybookPageSize,
	initialSearchQuery,
	pageSizes,
	onActivitiesChange,
}) => {
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use pagination
	const {
		items: activities,
		setItems: setActivities,
		searchQuery,
		searchField,
		pageSizeField,
		unslicedItems: allActivities,
		paginationComponent,
	} = usePagination<GetActivitiesResponse[0]>({
		cacheStorageKey: 'activities',
		pluralDescriptor: 'activities',
		initialItems: initialActivities,
		initialPageSize: storybookPageSize,
		initialSearchQuery: initialSearchQuery,
		pageSizes: pageSizes,
		searchableKeys: [
			'action',
			'details',
			'occurred',
			(activity) => (activity.user ? `${activity.user.firstName} ${activity.user.lastName}` : 'none'),
		],
		missingSearchableKeyFallbacks: {
			name: 'none',
		},
	});
	
	
	// Load activities on mount
	useEffect(() => {
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetActivitiesResponse>({
				action: 'GET',
				uri: `/reseller/${reseller?.id ?? ''}/activities`,
				completion: (json) => {
					setActivities(json.response);
				},
			})
		);
	}, [dispatch, setActivities, reseller?.id]);
	
	
	// Notify parent of activities change
	useEffect(() => {
		if (!allActivities) {
			return;
		}
		
		onActivitiesChange?.(allActivities);
	}, [allActivities, onActivitiesChange]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			{((activities && activities.length > 0) || searchQuery.length > 0) && (
				<Box>
					<MultiFieldContainer centerOnMobile grow={1} moreSpaceBetween>
						{searchField}
						{pageSizeField}
					</MultiFieldContainer>
				</Box>
			)}
			
			<Box>
				<Table>
					<thead>
						<tr>
							<th>Date</th>
							<th>Action</th>
							<th>User</th>
						</tr>
					</thead>
					
					<tbody>
						{activities?.map((activity) => <ActivityLogRow key={activity.id} activity={activity} />)}
						
						{activities?.length === 0 && (
							<tr>
								<td colSpan={999} style={{ textAlign: 'center' }}>
									<em>No {searchQuery.length > 0 ? 'matches found' : 'activities yet'}…</em>
								</td>
							</tr>
						)}
						
						{activities === null && <LoadingRow />}
					</tbody>
				</Table>
				
				{paginationComponent}
			</Box>
		</React.Fragment>
	);
};

export default ActivityLogTable;
